import React from 'react';
import { useLocale } from '../../lib/hooks/use-locale.ts';

export type FormatPercentageOptions = {
    locale: string;
    decimals?: number;
    showEmptyIfZero?: boolean;
    emptyText?: string;
    showPlusSignIfPositive?: boolean;
};

/**
 * Pure function to format a number as a percentage string.
 */
export const formatPercentage = (
    value: number | string | undefined | null,
    {
        locale,
        decimals = 1,
        emptyText = '-',
        showEmptyIfZero = false,
        showPlusSignIfPositive = false,
    }: FormatPercentageOptions,
): string => {
    const cleanValue = Number(value);

    if (Number.isNaN(cleanValue)) {
        return showEmptyIfZero ? emptyText : '0%';
    } else if (cleanValue === 0 && showEmptyIfZero) {
        return emptyText;
    } else {
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            signDisplay: showPlusSignIfPositive ? 'always' : 'auto',
        }).format(cleanValue);
    }
};

type Props = {
    children: number | string | undefined | null;
} & Omit<FormatPercentageOptions, 'locale'>;

export const PercentageOrEmpty: React.FC<Props> = ({
    children,
    decimals = 1,
    emptyText = '-',
    showEmptyIfZero = false,
    showPlusSignIfPositive = false,
}) => {
    const { locale } = useLocale();

    return (
        <>
            {formatPercentage(children, {
                locale,
                decimals,
                emptyText,
                showEmptyIfZero,
                showPlusSignIfPositive,
            })}
        </>
    );
};
